/**
 * @fileoverview Special Piece System - 特殊ピースシステム
 * 責務：特殊ピース生成・効果解決・定義管理
 */

import { ROWS, COLS } from '../../core/constants.js';
import { MinimalErrorTracker } from '../../shared/log.js';

import { getGameLogicDefinition, getAllDefinitions, getUIDefinition } from './definitions.js';

// =============================================================================
// 効果プラン - 統合版
// =============================================================================

function createEffectPlan() {
  return {
    removes: [], // [{ row, col }]
    transforms: [], // [{ row, col, to: { color, specialType? } }]
    creates: [], // [{ row, col, piece: { color, specialType? } }]
    score: 0, // numeric score delta
    triggers: [], // optional follow-up triggers (e.g., specials queued)
  };
}

function addRemove(plan, row, col) {
  plan.removes.push({ row, col });
}

function addTransform(plan, row, col, to) {
  plan.transforms.push({ row, col, to });
}

function addCreate(plan, row, col, piece) {
  plan.creates.push({ row, col, piece });
}

function mergeEffectPlans(base, addition) {
  const out = createEffectPlan();
  out.removes = dedupePositions([...(base.removes || []), ...(addition.removes || [])]);
  out.transforms = dedupePositions([...(base.transforms || []), ...(addition.transforms || [])]);
  out.creates = dedupePositions([...(base.creates || []), ...(addition.creates || [])]);
  out.score = (base.score || 0) + (addition.score || 0);
  out.triggers = [...(base.triggers || []), ...(addition.triggers || [])];
  return out;
}

function dedupePositions(arr) {
  const seen = new Set();
  const out = [];
  for (const item of arr || []) {
    if (!item || typeof item.row !== 'number' || typeof item.col !== 'number') continue;
    const key = `${item.row}-${item.col}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(item);
  }
  return out;
}

// =============================================================================
// 幾何計算 - 統合版
// =============================================================================

function isInBounds(row, col) {
  return row >= 0 && row < ROWS && col >= 0 && col < COLS;
}

function cross(row, col) {
  const positions = [];
  // 行全体
  for (let c = 0; c < COLS; c++) {
    positions.push({ row, col: c });
  }
  // 列全体（中心は重複するので除外）
  for (let r = 0; r < ROWS; r++) {
    if (r !== row) {
      positions.push({ row: r, col });
    }
  }
  return positions;
}

function area3x3(row, col) {
  const positions = [];
  for (let r = row - 1; r <= row + 1; r++) {
    for (let c = col - 1; c <= col + 1; c++) {
      if (isInBounds(r, c)) {
        positions.push({ row: r, col: c });
      }
    }
  }
  return positions;
}

function area5x5(row, col) {
  const positions = [];
  for (let r = row - 2; r <= row + 2; r++) {
    for (let c = col - 2; c <= col + 2; c++) {
      if (isInBounds(r, c)) {
        positions.push({ row: r, col: c });
      }
    }
  }
  return positions;
}

// =============================================================================
// 特殊ピース定義 - 統一定義システムに移行済み
// =============================================================================

// =============================================================================
// 効果解決
// =============================================================================

const EFFECT_RESOLVERS = {
  line: (board, piece, params) => {
    const plan = createEffectPlan();
    const [row, col] = piece.position;

    if (params.axis === 'row') {
      // 行全体を削除
      for (let c = 0; c < COLS; c++) {
        addRemove(plan, row, c);
      }
    } else {
      // 列全体を削除
      for (let r = 0; r < ROWS; r++) {
        addRemove(plan, r, col);
      }
    }
    return plan;
  },

  area: (board, piece, params) => {
    const plan = createEffectPlan();
    const [row, col] = piece.position;
    const positions = params.radius === 2 ? area5x5(row, col) : area3x3(row, col);

    for (const p of positions) {
      addRemove(plan, p.row, p.col);
    }
    return plan;
  },

  cross: (board, piece) => {
    const plan = createEffectPlan();
    const [row, col] = piece.position;
    const positions = cross(row, col);

    for (const p of positions) {
      addRemove(plan, p.row, p.col);
    }
    return plan;
  },

  stripe_bomb: (board, piece, params) => {
    const plan = createEffectPlan();
    const [row, col] = piece.position;

    // パラメータ優先: params.stripeAxis -> _mixParams -> デフォルト
    const axis = params.stripeAxis || piece._mixParams?.stripeAxis || 'row';

    if (axis === 'row') {
      // 横3行（中心±1行）
      for (let r = Math.max(0, row - 1); r <= Math.min(ROWS - 1, row + 1); r++) {
        for (let c = 0; c < COLS; c++) {
          addRemove(plan, r, c);
        }
      }
    } else {
      // 縦3列（中心±1列）
      for (let c = Math.max(0, col - 1); c <= Math.min(COLS - 1, col + 1); c++) {
        for (let r = 0; r < ROWS; r++) {
          addRemove(plan, r, c);
        }
      }
    }

    return plan;
  },
};

// =============================================================================
// 生成条件検証
// =============================================================================

function validateMatchCount(matchingPieces, expectedCount) {
  // undefinedの場合はこの条件をスキップ
  if (expectedCount === undefined) return true;
  return matchingPieces.length === expectedCount;
}

function validateMinMatchCount(matchingPieces, minCount) {
  // undefinedの場合はこの条件をスキップ
  if (minCount === undefined) return true;
  return matchingPieces.length >= minCount;
}

function validateDirection(matchingPieces, expectedDirection) {
  // undefinedの場合はこの条件をスキップ
  if (expectedDirection === undefined) return true;
  if (matchingPieces.length < 2) return false;

  const first = matchingPieces[0];
  const second = matchingPieces[1];
  const actualDirection = first.position[0] === second.position[0] ? 'row' : 'col';

  return actualDirection === expectedDirection;
}

const VALIDATORS = {
  matchCount: validateMatchCount,
  minMatchCount: validateMinMatchCount,
  direction: validateDirection,
};

function validateMatch(matchingPieces, definition) {
  // 検証対象を明確に限定
  const validationKeys = ['matchCount', 'minMatchCount', 'direction'];

  for (const key of validationKeys) {
    const value = definition[key];
    const validator = VALIDATORS[key];

    if (validator && value !== undefined) {
      const result = validator(matchingPieces, value);
      if (!result) {
        return false;
      }
    }
  }
  return true;
}

function getSortedDefinitions() {
  // 統一定義から全ての定義を取得してソート
  const allTypes = getAllDefinitions();
  const definitions = allTypes.map((type) => getGameLogicDefinition(type)).filter(Boolean);

  return definitions.sort((a, b) => {
    // 方向指定があるものを優先
    if (a.direction && !b.direction) return -1;
    if (!a.direction && b.direction) return 1;

    // より大きなマッチ数要求を優先
    const aCount = a.minMatchCount || a.matchCount || 0;
    const bCount = b.minMatchCount || b.matchCount || 0;
    return bCount - aCount;
  });
}

// =============================================================================
// 公開API
// =============================================================================

/**
 * 特殊ピース生成
 * @param {Array} matchingPieces - マッチしたピース配列
 * @returns {Object|null} 生成された特殊ピース
 */
export function generateSpecialPiece(matchingPieces) {
  if (!matchingPieces || matchingPieces.length === 0) return null;

  const definitions = getSortedDefinitions();

  for (const definition of definitions) {
    // 生成条件がない定義は通常マッチでは生成されない
    if (!definition.matchCount && !definition.minMatchCount && !definition.direction) {
      continue;
    }

    if (validateMatch(matchingPieces, definition)) {
      return {
        specialType: definition.specialType,
        color: 'special',
        definition,
      };
    }
  }

  return null;
}

/**
 * 特殊ピース効果解決
 * @param {Object} board - Boardインスタンス
 * @param {Object} piece - 特殊ピース
 * @returns {Object} 効果プラン
 */
export function resolveSpecialEffect(board, piece) {
  const definition = getGameLogicDefinition(piece.specialType);
  if (!definition) {
    MinimalErrorTracker.warn('GAME', `Unknown special type: ${piece.specialType}`);
    return createEffectPlan();
  }

  const resolver = EFFECT_RESOLVERS[definition.effectType];
  if (!resolver) {
    MinimalErrorTracker.warn('GAME', `Unknown effect type: ${definition.effectType}`);
    return createEffectPlan();
  }

  const plan = resolver(board, piece, definition.effectParams || {});

  // 変換処理: transformsToがあれば変換を追加
  if (definition.transformsTo) {
    addTransform(plan, piece.position[0], piece.position[1], {
      specialType: definition.transformsTo,
      color: 'special',
    });
  }

  // UI用トリガーを追加
  plan.triggers.push({
    action: 'special:triggered',
    row: piece.position[0],
    col: piece.position[1],
    specialType: piece.specialType,
    shouldVanish: !definition.transformsTo, // 変換する場合は消さない
  });

  return plan;
}

// getSpecialDefinition()削除 - 未使用のため

/**
 * UI メタデータ取得
 * @param {string} specialType - 特殊ピースタイプ
 * @returns {Object|null} UIメタデータ
 */
export function getSpecialUIMetadata(specialType) {
  // 統一定義からUI情報を取得
  const uiDef = getUIDefinition(specialType);
  return uiDef
    ? {
        cssClasses: uiDef.cssClasses,
        visualMode: 'special',
        className: uiDef.className,
      }
    : null;
}

// テスト用にexport
export {
  createEffectPlan,
  addRemove,
  addTransform,
  addCreate,
  mergeEffectPlans,
  cross,
  area3x3,
  area5x5,
};
